import { AuthedButton } from '../authed-button';
import { APP_CHAIN, CTF_ADDRESS, IS_DEBUG } from '@/client/confs/app';
import { useConditionResolved } from '@/client/hooks/use-on-chain';
import { consumeTransactionSuccess, watchTransaction } from '@/client/hooks/use-transaction-recovery';
import { useWallet } from '@/client/hooks/use-wallet';
import { markMarketClaimed } from '@/client/store/claim-store';
import {
  failTransactionRequest,
  finishTransactionRequest,
  isPreBroadcastGasFailure,
  startTransaction,
  sendTransaction,
  useTransactionStore,
} from '@/client/store/transaction-store';
import { getErrorMessage } from '@/client/utils';
import { api } from '@/client/utils/api';
import { publicClient } from '@/client/utils/chain-client';
import { refetchQuery, refetchSignedUserAssets, refetchUserPositions } from '@/client/utils/query-status';
import { useMarketDetail } from '@/libs/react-sdk/api/use-market-detail';
import { USER_HOLDING_POSITIONS_QUERY } from '@/libs/react-sdk/api/use-user-holding-positions';
import { MarketStatus } from '@/libs/sdk/api';
import { predictionAbi } from '@/libs/sdk/contracts/abis/prediction';
import confetti from 'canvas-confetti';
import { useEffect, useRef, useState, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Address } from 'viem';

// CTF redeemPositions ABI (for PredictionCTF markets)
const redeemPositionsAbi = [
  {
    type: 'function' as const,
    name: 'redeemPositions' as const,
    stateMutability: 'nonpayable' as const,
    inputs: [
      { name: 'collateralToken', type: 'address' as const },
      { name: 'parentCollectionId', type: 'bytes32' as const },
      { name: 'conditionId', type: 'bytes32' as const },
      { name: 'indexSets', type: 'uint256[]' as const },
    ],
    outputs: [],
  },
] as const;

type ClaimButtonProps = {
  marketAddress: string;
  optionAddress: string;
  disabled?: boolean;
};
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const ClaimButton = ({ marketAddress, optionAddress, disabled = false }: ClaimButtonProps) => {
  const [claimed, setClaimed] = useState(false);
  const [isTrading, setIsTrading] = useState(false);
  const { t } = useTranslation();
  const { walletClient } = useWallet();
  const mounted = useRef(true);
  const latest = useRef({ walletClient, marketAddress });
  latest.current = { walletClient, marketAddress };
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const activeTransaction = useTransactionStore(state =>
    walletClient?.account ? state.findActive(APP_CHAIN.id, walletClient.account.address) : undefined
  );
  const market = useMarketDetail(api, { marketAddress });
  // redeemPositions collateral MUST be this market's own base token — using the
  // global toggle token on a mixed multi-base site would redeem nothing
  const baseTokenAddress = market.data?.baseTokenAddress;
  const conditionId = market.data?.conditionId as `0x${string}` | undefined;

  // On-chain source of truth: is this condition resolved? (payoutDenominator > 0)
  const { data: isResolvedOnChain } = useConditionResolved(CTF_ADDRESS as Address | undefined, conditionId);
  // Claim is allowed if chain says resolved, regardless of backend marketStatus
  const canClaim = CTF_ADDRESS ? isResolvedOnChain === true : market.data?.marketStatus === MarketStatus.closed;

  const handleClaim = async () => {
    if (!walletClient?.account) {
      toast.error(t('wallet-not-connected', 'Please connect your wallet'));
      return false;
    }
    const capturedAccount = walletClient.account.address;
    if (walletClient.chain?.id !== APP_CHAIN.id) return false;
    const requestId = startTransaction({
      id: crypto.randomUUID(),
      chainId: APP_CHAIN.id,
      account: capturedAccount,
      operation: 'claim',
      marketAddress: marketAddress as Address,
      step: 'execute',
      stage: 'wallet',
      startedAt: Date.now(),
    });
    if (!requestId) return false;
    try {
      setIsTrading(true);

      const account = walletClient.account;
      let txHash: `0x${string}`;
      const conditionId = market.data?.conditionId as `0x${string}` | undefined;

      // Guard: ensure market data matches the target market (prevents stale cache from
      // sending the wrong conditionId when user switches markets quickly in wallet panel)
      if (market.data?.marketAddress && market.data.marketAddress !== marketAddress) {
        toast.error(t('claim-data-stale', 'Market data is loading. Please try again.'));
        return false;
      }

      if (CTF_ADDRESS && !baseTokenAddress) {
        toast.error(t('claim-data-incomplete', 'Market data incomplete. Please contact support.'));
        return false;
      }

      if (CTF_ADDRESS && !conditionId) {
        // PredictionCTF market but conditionId not synced — cannot use legacy claimAll
        toast.error(t('claim-data-incomplete', 'Market data incomplete. Please contact support.'));
        return false;
      }

      if (CTF_ADDRESS && conditionId) {
        // PredictionCTF path: redeem via ConditionalTokens.redeemPositions
        const outcomeCount = market.data?.outcomeSlotCount ?? 2;
        const indexSets = Array.from({ length: outcomeCount }, (_, i) => BigInt(1 << i));
        const redeemArgs = {
          address: CTF_ADDRESS,
          abi: redeemPositionsAbi,
          functionName: 'redeemPositions' as const,
          args: [
            baseTokenAddress as Address,
            '0x0000000000000000000000000000000000000000000000000000000000000000' as `0x${string}`,
            conditionId,
            indexSets,
          ] as const,
          account,
        };
        const redeemGas = await publicClient.estimateContractGas(redeemArgs);
        if (
          !mounted.current ||
          latest.current.walletClient?.account?.address !== capturedAccount ||
          latest.current.marketAddress !== marketAddress
        )
          return false;
        txHash = await sendTransaction(requestId, () =>
          walletClient.writeContract({
            ...redeemArgs,
            chain: walletClient.chain,
            gas: (redeemGas * 130n) / 100n,
          })
        );
      } else {
        // Legacy Prediction path: claimAll
        const claimArgs = {
          address: marketAddress as Address,
          abi: predictionAbi,
          functionName: 'claimAll' as const,
          args: [] as const,
          account,
        };
        const claimGas = await publicClient.estimateContractGas(claimArgs);
        if (
          !mounted.current ||
          latest.current.walletClient?.account?.address !== capturedAccount ||
          latest.current.marketAddress !== marketAddress
        )
          return false;
        txHash = await sendTransaction(requestId, () =>
          walletClient.writeContract({
            ...claimArgs,
            chain: walletClient.chain,
            gas: (claimGas * 130n) / 100n,
          })
        );
      }
      await watchTransaction(requestId, txHash);
      if (
        !mounted.current ||
        latest.current.walletClient?.account?.address.toLowerCase() !== capturedAccount.toLowerCase() ||
        latest.current.walletClient?.chain?.id !== APP_CHAIN.id ||
        latest.current.marketAddress !== marketAddress ||
        !consumeTransactionSuccess(requestId)
      )
        return false;

      toast.success(t('claim-success'));
      setClaimed(true);
      markMarketClaimed(capturedAccount, marketAddress);

      // Refresh related queries so UI updates automatically
      await Promise.allSettled([
        refetchQuery('MarketDetail'),
        refetchUserPositions(),
        refetchSignedUserAssets(),
        refetchQuery('UserAssetsInfo'),
        refetchQuery(USER_HOLDING_POSITIONS_QUERY),
        // the payout lands in the wallet; the header balance polls only every 60 s
        refetchQuery('SingerTokenBalance'),
        // the claim burns the position; MarketResult's on-chain read polls every 60 s
        refetchQuery('OnChainPosition'),
      ]);

      return true;
    } catch (error) {
      if (error instanceof Error && error.message === 'transaction-superseded') return false;
      if (IS_DEBUG) console.log('claim error', error);
      const msg = isPreBroadcastGasFailure(error) ? 'transaction-insufficient-gas' : getErrorMessage(error);
      toast.error(msg?.startsWith('transaction-') ? t(msg) : (msg ?? t('claim-failed')));
      return false;
    } finally {
      finishTransactionRequest(requestId);
      failTransactionRequest(requestId);
      if (mounted.current) setIsTrading(false);
    }
  };
  const handleClick = async (event: MouseEvent<HTMLButtonElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = rect.left + rect.width / 2;
    const y = rect.top + rect.height / 2;
    const success = await handleClaim();
    if (success) {
      confetti({
        origin: {
          x: x / window.innerWidth,
          y: y / window.innerHeight,
        },
      });
    }
  };
  if (claimed)
    return (
      <AuthedButton
        disabled={true}
        variant="default"
        size="lg"
        className="mt-4 p-4 text-center text-sm rounded-3 font-semibold leading-none w-full opacity-60"
      >
        已領取 ✓
      </AuthedButton>
    );
  return (
    <AuthedButton
      disabled={isTrading || !!activeTransaction || disabled || !canClaim}
      onClick={handleClick}
      isLoading={isTrading || market.isLoading}
      variant="default"
      size="lg"
      className="mt-4 p-4 text-center text-sm rounded-3 font-semibold leading-none w-full"
    >
      {canClaim
        ? t('claim-winnings')
        : market.data?.marketStatus === MarketStatus.disputed
          ? t('dispute-in-progress', '爭議中，等待仲裁結果')
          : t('processing-settlement', '結算中，請等待')}
    </AuthedButton>
  );
};
